import {
  KPIBucket,
  TopicCluster,
  Recommendation,
  Alert,
  FormTemplate,
  User,
} from './types'

export const USERS: User[] = [
  {
    id: 'u1',
    name: 'Alex Employee',
    role: 'employee',
    email: 'alex@company.com',
  },
  { id: 'u2', name: 'Sarah HR', role: 'hr', email: 'sarah@company.com' },
  {
    id: 'u3',
    name: 'Mike Exec',
    role: 'management',
    email: 'mike@company.com',
  },
  { id: 'u4', name: 'Admin User', role: 'admin', email: 'admin@company.com' },
]

export const KPI_BUCKETS: KPIBucket[] = [
  {
    id: 'k1',
    name: 'Workload & Burnout',
    score: 68,
    trend: -5,
    previousScore: 73,
    description: 'Employee perception of work volume and stress levels.',
    signals: [
      'Overtime frequency mentions',
      'Deadline pressure complaints',
      'Work-life balance concerns',
      'Capacity and staffing requests',
    ],
  },
  {
    id: 'k2',
    name: 'Leadership & Management',
    score: 78,
    trend: 2,
    previousScore: 76,
    description:
      'Effectiveness and support from direct managers and leadership.',
    signals: [
      'Manager feedback quality',
      'Decision transparency mentions',
      'Support and guidance requests',
    ],
  },
  {
    id: 'k3',
    name: 'Communication',
    score: 72,
    trend: 0,
    previousScore: 72,
    description: 'Clarity and frequency of internal communication.',
    signals: [
      'Information flow complaints',
      'Meeting effectiveness feedback',
      'Cross-team coordination issues',
    ],
  },
  {
    id: 'k4',
    name: 'Culture & Belonging',
    score: 85,
    trend: 4,
    previousScore: 81,
    description: 'Sense of community and alignment with company values.',
    signals: [
      'Team bonding mentions',
      'Inclusion and diversity feedback',
      'Values alignment comments',
    ],
  },
  {
    id: 'k5',
    name: 'Career Growth',
    score: 62,
    trend: -2,
    previousScore: 64,
    description: 'Opportunities for advancement and professional development.',
    signals: [
      'Promotion clarity questions',
      'Learning opportunity requests',
      'Career path discussions',
    ],
  },
  {
    id: 'k6',
    name: 'Compensation & Benefits',
    score: 70,
    trend: 1,
    previousScore: 69,
    description: 'Satisfaction with pay and benefits package.',
    signals: [
      'Salary comparison mentions',
      'Benefits usage feedback',
      'Equity and fairness concerns',
    ],
  },
  {
    id: 'k7',
    name: 'Processes & Tools',
    score: 58,
    trend: -8,
    previousScore: 66,
    description: 'Efficiency of workflows and software tools.',
    signals: [
      'Tool frustration reports',
      'Workflow bottleneck mentions',
      'Automation requests',
    ],
  },
  {
    id: 'k8',
    name: 'Wellbeing',
    score: 74,
    trend: 3,
    previousScore: 71,
    description: 'Physical and mental health support.',
    signals: [
      'Mental health mentions',
      'Stress level indicators',
      'Wellness program feedback',
    ],
  },
  {
    id: 'k9',
    name: 'Other',
    score: 80,
    trend: 0,
    previousScore: 80,
    description: 'Miscellaneous feedback.',
    signals: ['General suggestions', 'Miscellaneous feedback'],
  },
]

export const TOPIC_CLUSTERS: TopicCluster[] = [
  {
    id: 't1',
    name: 'Burnout in Sales Team',
    mentionCount: 23,
    sentimentRatio: 0.15,
    trend: 'up',
    kpiBucketId: 'k1',
    type: 'concern',
  },
  {
    id: 't2',
    name: 'Cross-dept Communication Gaps',
    mentionCount: 19,
    sentimentRatio: 0.3,
    trend: 'up',
    kpiBucketId: 'k3',
    type: 'concern',
  },
  {
    id: 't3',
    name: 'Unclear Promotion Criteria',
    mentionCount: 15,
    sentimentRatio: 0.2,
    trend: 'stable',
    kpiBucketId: 'k5',
    type: 'concern',
  },
  {
    id: 't4',
    name: 'Legacy CRM Issues',
    mentionCount: 12,
    sentimentRatio: 0.1,
    trend: 'down',
    kpiBucketId: 'k7',
    type: 'concern',
  },

  {
    id: 't5',
    name: 'New Mentorship Program',
    mentionCount: 18,
    sentimentRatio: 0.9,
    trend: 'up',
    kpiBucketId: 'k5',
    type: 'improvement',
  },
  {
    id: 't6',
    name: 'Flexible Work Policy',
    mentionCount: 21,
    sentimentRatio: 0.95,
    trend: 'up',
    kpiBucketId: 'k8',
    type: 'improvement',
  },
  {
    id: 't7',
    name: 'Quarterly All-Hands',
    mentionCount: 14,
    sentimentRatio: 0.8,
    trend: 'stable',
    kpiBucketId: 'k3',
    type: 'improvement',
  },
]

export const RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'r1',
    relatedTopicId: 't1',
    level: 'HR',
    title: 'Address Sales Team Burnout',
    whyItMatters:
      'High turnover risk detected in high-performing sales unit due to sustained overtime.',
    actionItems: [
      'Conduct workload audit for Q3',
      'Review quota expectations vs market reality',
      'Implement mandatory "unplugged" days',
    ],
    expectedImpact: 'High',
    timeHorizon: 'Short-term',
  },
  {
    id: 'r2',
    relatedTopicId: 't2',
    level: 'Exec',
    title: 'Strategic Communication Overhaul',
    whyItMatters:
      'Silos between Product and Sales are causing missed revenue opportunities.',
    actionItems: [
      'Introduce cross-functional weekly syncs',
      'Standardize internal update formats',
      'Train managers in clarity frameworks',
    ],
    expectedImpact: 'High',
    timeHorizon: 'Mid-term',
  },
  {
    id: 'r3',
    relatedTopicId: 't4',
    level: 'HR',
    title: 'Tooling Efficiency Review',
    whyItMatters:
      'Employees report losing 4+ hours/week due to legacy CRM latency.',
    actionItems: [
      'Survey team for specific pain points',
      'Evaluate budget for CRM upgrade',
      'Provide interim training shortcuts',
    ],
    expectedImpact: 'Medium',
    timeHorizon: 'Mid-term',
  },
  {
    id: 'r4',
    relatedTopicId: 't3',
    level: 'HR',
    title: 'Clarify Promotion Pathways',
    whyItMatters:
      'Lack of clarity is driving disengagement among high-potential mid-level employees.',
    actionItems: [
      'Publish transparent career ladders for all departments',
      'Train managers on having career conversations',
      'Host a Q&A session on the promotion process',
    ],
    expectedImpact: 'High',
    timeHorizon: 'Mid-term',
  },
  {
    id: 'r5',
    relatedTopicId: 't5',
    level: 'HR',
    title: 'Scale Mentorship Program',
    whyItMatters:
      'Early success indicates high demand; formalizing will improve retention.',
    actionItems: [
      'Recruit more senior mentors',
      'Create a structured mentorship guide',
      'Implement feedback loops for pairs',
    ],
    expectedImpact: 'Medium',
    timeHorizon: 'Long-term',
  },
  {
    id: 'r6',
    relatedTopicId: 't6',
    level: 'HR',
    title: 'Expand Flexible Work Options',
    whyItMatters:
      'Flexibility is a key driver of satisfaction and talent attraction.',
    actionItems: [
      'Measure productivity impact of current policy',
      'Survey employees on desired flexibility options',
      'Pilot a "work from anywhere" week',
    ],
    expectedImpact: 'High',
    timeHorizon: 'Mid-term',
  },
  {
    id: 'r7',
    relatedTopicId: 't7',
    level: 'HR',
    title: 'Enhance All-Hands Format',
    whyItMatters:
      'Engagement is stable but could be higher with more interactive elements.',
    actionItems: [
      'Incorporate live Q&A with leadership',
      'Showcase employee wins and stories',
      'Shorten presentation segments',
    ],
    expectedImpact: 'Low',
    timeHorizon: 'Short-term',
  },
]

export const ALERTS: Alert[] = [
  {
    id: 'a1',
    type: 'concern_spike',
    explanation:
      'Sudden spike in "Burnout" mentions within Sales Dept (last 48h).',
    severity: 'critical',
    timestamp: '2 hours ago',
    kpiBucketId: 'k1',
  },
  {
    id: 'a2',
    type: 'kpi_drop',
    explanation: 'Processes & Tools score dropped by 8% this month.',
    severity: 'warning',
    timestamp: '1 day ago',
    kpiBucketId: 'k7',
  },
]

export const FORM_TEMPLATES: FormTemplate[] = [
  {
    id: 'f1',
    title: 'Q3 Employee Pulse',
    status: 'not_started',
    dueDate: 'Oct 15',
  },
  { id: 'f2', title: 'Manager Effectiveness Survey', status: 'submitted' },
  { id: 'f3', title: 'Benefits Satisfaction', status: 'not_started' },
]
